import { ReactElement, ReactNode, ReactText, useRef } from 'react';
import { toast } from 'react-toastify';
import { __BACKEND_URL__ } from 'shell/constants/environment';
import {
  HttpBackendError,
  UnknownBackendErrorCode,
} from 'shell/error/definitions/http-backend-error';
import { useErrorHandler } from 'shell/error/hooks/use-error-handler';
import { useHttpLoading } from 'shell/hooks/use-http-loading';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { HttpStatusCode } from 'shell/types/http-status-code';
import { CachePolicies, IncomingOptions, Provider } from 'use-http';

interface HttpProviderProps {
  children: ReactNode;
}

export const HttpProvider = ({ children }: HttpProviderProps): ReactElement => {
  const httpLoading = useHttpLoading();
  const { setError } = useErrorHandler();
  const { setUserProfile } = useUserProfile();

  const toastAuthentication = useRef<ReactText>('');

  const httpProviderOpts: IncomingOptions = {
    cachePolicy: CachePolicies.NO_CACHE,
    interceptors: {
      request: async ({ options }) => {
        // TODO: Add support to skip global loading for a given request.
        httpLoading.addLoading();
        return options;
      },
      response: async ({ response }) => {
        return new Promise((resolve, reject) => {
          // In case an error is found in the context of a 200 http status code, a reject
          // promise is returned so it can be handled by the "onError" hook.
          // NOTE: There is no need to remove the loading, the "onError" will take care of it.
          if (response.data?.summary?.error) {
            // TODO: Add support to skip global error handling for a given request.
            const { code, messages } = response.data?.summary;
            const backError = new HttpBackendError(code, messages, HttpStatusCode.Ok, true);
            return reject(backError);
          }
          // TODO: Add support to skip global loading for a given request.
          httpLoading.removeLoading();
          resolve(response);
        });
      },
    },
    onAbort: () => {
      // TODO: Add support to skip global loading for a given request.
      httpLoading.removeLoading();
    },
    onError: ({ error }) => {
      let backendError: HttpBackendError | Error = error;
      if (!(backendError instanceof HttpBackendError)) {
        const statusCode = Number(error.name);
        const messages = [error.message];
        backendError = new HttpBackendError(UnknownBackendErrorCode, messages, statusCode, true);
      }

      if ((backendError as HttpBackendError).statusCode === HttpStatusCode.Unauthorized) {
        setUserProfile();
        if (!toast.isActive(toastAuthentication.current)) {
          toastAuthentication.current = toast.error(
            'Non autorizzato, si prega di ripetere il login'
          );
        }
      } else {
        setError(backendError as HttpBackendError);
      }

      // TODO: Add support to skip global loading for a given request.
      httpLoading.removeLoading();
      throw error;
    },
  };

  return (
    <Provider options={httpProviderOpts} url={__BACKEND_URL__}>
      {children}
    </Provider>
  );
};

HttpProvider.displayName = 'HttpProvider';
